/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import { useStore } from './stores/common';
import { Vector3 } from 'three';
import 'firebase/firestore';
import 'firebase/storage';
import i18n from './i18n/i18n';
import { ProjectState, MolecularContainer, MoleculeInterface, ProjectInfo, PublicProjectInfo } from './types';
import { ProjectType } from './constants';
import dayjs from 'dayjs';
import { ModelUtil } from './models/ModelUtil.ts';
import { useDataStore } from './stores/commonData.ts';
import { setMessage } from './helpers.tsx';
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { firestore, storage } from './firebase.ts';
import { deleteObject, getDownloadURL, listAll, ref as storageRef, uploadString } from 'firebase/storage';
import {
  mergeWithDefaults,
  migrateBiomoleculesField,
  migrateRetiredAiProvider,
  migrateUnnamedCrystals,
  fillEmptyLabFromGallery,
  padHiddenPropertiesForLegacyProjects,
  PROJECT_DEFAULTS,
} from './project/ProjectUtil.ts';
import { CHAT_GZ_FIELD, decodeChatMessages, toUint8 } from './project/chatCompression.ts';
import { BIO_GZ_FIELD, decodeBiomoleculeData } from './biomolecule/biomoleculeCompression.ts';
import { Atom } from './models/Atom.ts';
import { Molecule } from './models/Molecule.ts';
import { applyAtomChargesToAtoms, rehydratePartialChargesFromSdf } from './gasteigerCharges.ts';
import { rehydrateComputedPropertiesFromSdf } from './computedProperties.ts';
import { cancelCurrentGeneration } from './generationAbort.ts';
import { clearAgentChatAttachments } from './agent/chatAttachments.ts';

// Atom fields that are runtime-only (recomputed on load) and should never reach Firestore.
const ATOM_FIELDS_TO_STRIP = [
  'index',
  'mass',
  'sigma',
  'epsilon',
  'charge',
  'force',
  'acceleration',
  'displacement',
  'initialPosition',
  'initialVelocity',
  'bead', // re-applied on load from the molecule's bead model
] as const satisfies readonly (keyof Atom)[];

// Molecule fields stripped on save. `radialBonds` / `angularBonds` /
// `torsionalBonds` are rebuilt at chamber-load time from the SDF, so
// they round-trip through Firestore as derived data rather than stored
// data. `center` and `multipleResidues` are derivable from positions.
//
// `data` (the SDF string) is the canonical source of topology for any
// molecule without a `url` — including all AI-generated molecules and
// every polymer chain built by polymerBuilder. Stripping it on save
// means the chamber has atoms but no SDF on reload, the SDF parser is
// never invoked, the bond list stays empty, and atoms fly apart under
// NVE because nothing constrains them. We keep `data` for any molecule
// missing a `url`; URL-fetched molecules can drop it since the loader
// re-fetches.
const MOLECULE_FIELDS_TO_STRIP_REMOTE_ALWAYS = [
  'radialBonds',
  'angularBonds',
  'torsionalBonds',
  'center',
  'multipleResidues',
] as const satisfies readonly (keyof Molecule)[];

// ProjectState fields whose default is falsy. Stripped on save to keep Firestore
// docs compact; PROJECT_DEFAULTS in fetchProject restores them on load.
const PS_FIELDS_TO_STRIP_IF_FALSY = [
  'hideGallery',
  'isPublic',
  'description',
  'selectedMolecule',
  'selectedProperty',
  'autoscaleGraph',
  'sortDescending',
  'xLinesScatterPlot',
  'yLinesScatterPlot',
  'angularBondsVisible',
  'torsionalBondsVisible',
  'chamberViewerAxes',
  'chamberViewerFoggy',
  'xyPlaneVisible',
  'yzPlaneVisible',
  'xzPlaneVisible',
  'gravitationalAcceleration',
  'gravityDirection',
  'pistonEnabled',
  'pistonPressure',
  // NOT 'pistonMass': 0 is a meaningful setting there (auto/HEAVY plate), not
  // "unset" — stripping it reloaded as the light 100 amu default.
  'pistonLangevin',
  'quadrupoleSites',
  'dispersionTail',
  'pistonDamping',
  'pistonSnapshot',
  'molecularContainerVisible',
  'momentumVisible',
  'forceVisible',
  'vdwBondsVisible',
  'energyGraphVisible',
  'speedGraphVisible',
  'irSpectrumPanelVisible',
  'analysisPanelVisible',
  'activeAnalysisTab',
  'measurementTracks',
  'speedGraphMaxX',
  'speedGraphMaxY',
  'speedGraphSortByMolecule',
  'speedGraphBinNumber',
  'constantTemperature',
  'constantPressure',
  'navigationView',
  'showInstructionPanel',
  'spaceshipThrust',
] as const satisfies readonly (keyof ProjectState)[];

// ProjectState fields used only by drug-discovery projects — dropped on save
// when the project is molecular-modeling.
const PS_FIELDS_DRUG_DISCOVERY_ONLY = [
  'protein',
  'ligand',
  'ligandTransform',
  'ligandVelocity',
  'spaceshipDisplayMode',
  'spaceshipX',
  'spaceshipY',
  'spaceshipZ',
  'spaceshipSize',
  'spaceshipPitch',
  'spaceshipRoll',
  'spaceshipYaw',
] as const satisfies readonly (keyof ProjectState)[];

// Copies live position/velocity/force-field params from `source` into `dest`
// (Firestore can't roundtrip Vector3, so we project to plain coords). When
// `stripRemoteFields`, also drops bonds/center/data from each `dest` molecule.
// Copy src's coordinates into dst in place, or make a new vector of the same
// kind as `like` (a Vector3 beside a Vector3, a plain object in a JSON clone).
const copyVec3 = (
  dst: Vector3 | undefined,
  src: { x: number; y: number; z: number },
  like: { x: number; y: number; z: number },
): Vector3 => {
  if (dst) {
    dst.x = src.x;
    dst.y = src.y;
    dst.z = src.z;
    return dst;
  }
  return like instanceof Vector3 ? new Vector3(src.x, src.y, src.z) : ({ x: src.x, y: src.y, z: src.z } as Vector3);
};

export const syncMoleculesFromRef = (dest: Molecule[], source: Molecule[], stripRemoteFields: boolean) => {
  for (const [i, m] of dest.entries()) {
    const currentMol = source[i];
    if (!currentMol) continue;
    for (const [j, a] of m.atoms.entries()) {
      const b = currentMol.atoms[j];
      if (!b) continue;
      a.position.x = b.position.x;
      a.position.y = b.position.y;
      a.position.z = b.position.z;
      a.velocity.x = b.velocity.x;
      a.velocity.y = b.velocity.y;
      a.velocity.z = b.velocity.z;
      a.sigma = b.sigma;
      a.epsilon = b.epsilon;
      a.mass = b.mass;
      a.charge = b.charge;
      if (b.chargeSet) a.chargeSet = true;
      a.fixed = b.fixed;
      // Gay-Berne rods: the axis and its spin travel with the atom. Field by field,
      // not .copy(): Save's dest is a JSON clone, whose vectors are plain {x, y, z}
      // objects without Vector3's methods (.copy threw there, before the save's
      // try, and the save spun forever on any project with rods).
      if (b.orientation) {
        a.orientation = copyVec3(a.orientation, b.orientation, a.position);
        if (b.angularVelocity) a.angularVelocity = copyVec3(a.angularVelocity, b.angularVelocity, a.position);
        if (b.initialOrientation)
          a.initialOrientation = copyVec3(a.initialOrientation, b.initialOrientation, a.position);
      }
      // The restraint ANCHOR moves with the atom (gizmo drag, Translate / Rotate,
      // the arrow keys, transformLabMolecule all move the live anchor); without
      // this the saved project kept the anchor where the restraint was first set.
      if (a.restraint && b.restraint) {
        a.restraint.position.x = b.restraint.position.x;
        a.restraint.position.y = b.restraint.position.y;
        a.restraint.position.z = b.restraint.position.z;
      }
    }
    if (stripRemoteFields) {
      const mp = m as Partial<Molecule>;
      for (const f of MOLECULE_FIELDS_TO_STRIP_REMOTE_ALWAYS) delete mp[f];
      // Only strip the SDF text when the molecule can be re-fetched from
      // its `url`. Without that fallback (AI-generated molecules, every
      // polymer-builder chain) `data` IS the topology — stripping it
      // produces a bond-less skeleton at reload.
      if (m.url) delete mp.data;
    }
  }
};

// Mutates a deep clone of ProjectState into the shape we write to Firestore:
// drops transient atom fields, falsy-default fields, and fields that don't
// apply to the current project type. No-op for non-molecular-modeling saves.
export const stripProjectStateForSave = (ps: ProjectState) => {
  // Drug-discovery receptors are full PDB structures. A protein with >10k
  // atoms carries a `data` blob big enough on its own to push the document
  // past Firestore's per-document/value size limit, and the write is
  // rejected with "Property protein contains an invalid nested entity". Drop
  // the inline blob when the receptor is re-fetchable from its `url` (the
  // RCSB ".pdb" download link set by the PDB-id path, or a bundled internal
  // asset resolved by name on load) — DockingViewer's loader re-fetches it
  // from the url on open, exactly as it already does for url-only internal
  // proteins. Same rule for a url-backed ligand. A receptor supplied as raw
  // data with no url keeps its blob so it survives the round-trip. This runs
  // before the molecular-modeling early-return because protein/ligand only
  // exist on drug-discovery projects.
  if (ps.protein?.data && ps.protein.url) delete ps.protein.data;
  if (ps.ligand?.data && ps.ligand.url) delete ps.ligand.data;
  // Same rule for each Biomolecular Lab structure in the curated set: an
  // RCSB-fetched protein/DNA/RNA carries a multi-hundred-KB PDB blob that can
  // push the doc past Firestore's size limit. When it came from a PDB ID (so a
  // `url` is set), drop the inline blob — the viewer re-fetches it on open.
  if (ps.biomolecules) {
    for (const b of ps.biomolecules) if (b?.data && b.url) delete b.data;
  }
  if (ps.biomolecule?.data && ps.biomolecule.url) delete ps.biomolecule.data; // legacy field

  // The chat log is persisted as a gzip'd Bytes field (CHAT_GZ_FIELD) the save
  // path attaches separately; never write the bulky plain array to the doc.
  // This runs for every project type (chat exists on all of them), before the
  // molecular-modeling early-return below.
  delete (ps as Partial<ProjectState>).chatMessages;

  // Improper (out-of-plane) bonds are DERIVED data: Molecule.clone regenerates
  // them from topology via buildMmffImpropers on every load and never reads the
  // persisted copy. But each one holds four full Atom references, so serialized
  // it balloons to ~1.3 KB apiece (~43 KB per molecule) — the dominant cause of
  // project docs blowing past Firestore's 1 MiB limit. Only the lab molecules
  // (full Molecule instances) carry them; gallery `molecules` are lightweight
  // MoleculeInterface records with no bond arrays.
  for (const m of ps.testMolecules ?? []) {
    delete (m as unknown as { improperBonds?: unknown }).improperBonds;
  }

  if (ps.type !== ProjectType.MOLECULAR_MODELING && ps.type !== ProjectType.POLYMER_BUILDER) return;

  for (const m of ps.testMolecules) {
    for (const a of m.atoms) {
      const ap = a as Partial<Atom>;
      // A USER-set charge is project data, not a runtime value: keep it (the
      // load path re-derives the computed ones and leaves these alone).
      const userCharge = a.chargeSet ? a.charge : undefined;
      for (const f of ATOM_FIELDS_TO_STRIP) delete ap[f];
      if (userCharge !== undefined) a.charge = userCharge;
      else delete ap.chargeSet;
      if (!a.fixed) delete a.fixed;
      if (!a.damp) delete a.damp;
      if (!a.trajectory) delete a.trajectory;
      if (a.restraint && a.restraint.strength === 0) delete a.restraint;
    }
  }

  const psPartial = ps as Partial<ProjectState>;
  for (const f of PS_FIELDS_TO_STRIP_IF_FALSY) {
    if (!ps[f]) delete psPartial[f];
  }
  for (const f of PS_FIELDS_DRUG_DISCOVERY_ONLY) {
    delete psPartial[f];
  }
  // selectedMolecule.data is large; drop it even when selectedMolecule is kept
  if (ps.selectedMolecule) delete ps.selectedMolecule.data;
};

// --- Project metadata index --------------------------------------------------
// The "My Projects" list only needs {title, type, timestamp, isPublic} per
// project. Scanning the whole users/{uid}/projects subcollection to get those
// few fields downloaded every multi-MB project document, so list latency scaled
// with total stored bytes. Instead we keep a compact map on the user doc, keyed
// by title, written atomically alongside each project save (see the writeBatch
// paths in useCloudProjects). Listing is then a single small read.
//
// Titles are constrained by REGEX_ALLOWABLE_IN_NAME, which excludes every
// character Firestore forbids in a map key (. / ~ * [ ]), so a title is a valid
// key as-is. All writes go through setDoc(..., {merge:true}): the deep merge
// leaves sibling entries untouched and creates the user doc / projectIndex map
// when absent, so brand-new accounts (whose users/{uid} doc may be only a
// phantom over the projects subcollection) need no pre-created parent doc.
export interface ProjectIndexEntry {
  timestamp: number;
  type: ProjectType;
  isPublic?: true;
}

// Map a ProjectInfo to its stored index value, omitting isPublic unless true
// (Firestore rejects `undefined` inside the written map).
const toProjectIndexEntry = (info: ProjectInfo): ProjectIndexEntry => {
  const entry: ProjectIndexEntry = { timestamp: info.timestamp, type: info.type };
  if (info.isPublic === true) entry.isPublic = true;
  return entry;
};

// Merge payload that writes exactly one project's index slot. Reused by the
// batched save paths via batch.set(userRef, ..., { merge: true }).
export const projectIndexSetPayload = (info: ProjectInfo) => ({
  projectIndex: { [info.title]: toProjectIndexEntry(info) },
});

export const setProjectIndexEntry = async (uid: string, info: ProjectInfo) => {
  try {
    await setDoc(doc(firestore, 'users', uid), projectIndexSetPayload(info), { merge: true });
  } catch (error) {
    console.error('Failed to update project index entry:', error);
  }
};

export const removeProjectIndexEntry = async (uid: string, title: string) => {
  try {
    await setDoc(doc(firestore, 'users', uid), { projectIndex: { [title]: deleteField() } }, { merge: true });
  } catch (error) {
    console.error('Failed to remove project index entry:', error);
  }
};

// Read the compact project index off the user doc. Returns null when the field
// is absent (account predates the index → the caller should reconcile once).
export const readProjectIndex = async (uid: string): Promise<ProjectInfo[] | null> => {
  const snap = await getDoc(doc(firestore, 'users', uid));
  const index = snap.data()?.projectIndex as Record<string, ProjectIndexEntry> | undefined;
  if (!index) return null;
  const out: ProjectInfo[] = [];
  for (const [title, entry] of Object.entries(index)) {
    const info: ProjectInfo = { title, timestamp: entry.timestamp, type: entry.type };
    if (entry.isPublic === true) info.isPublic = true;
    out.push(info);
  }
  return out;
};

// Rebuild the index from the authoritative projects subcollection and write it
// back. This is both the one-time backfill for pre-index accounts and the
// manual repair path if the index ever drifts. It pays the old full-scan cost
// once; subsequent listings use the single-read readProjectIndex. The field is
// cleared first so projects deleted out-of-band don't linger (a plain merge
// would only add/overwrite keys, never remove them).
export const reconcileProjectIndex = async (uid: string): Promise<ProjectInfo[]> => {
  const projectsCol = collection(firestore, 'users', uid, 'projects');
  const snapshot = await getDocs(projectsCol);
  const list: ProjectInfo[] = [];
  const map: Record<string, ProjectIndexEntry> = {};
  // Trashed docs (soft-deleted) stay in the subcollection but must NOT land in
  // projectIndex (that would resurrect them into "My Projects"). Sort them into
  // the parallel trashIndex instead, so a reconcile rebuilds both maps in step.
  const trashMap: Record<string, TrashIndexEntry> = {};
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    const info: ProjectInfo = { title: docSnap.id, timestamp: data.timestamp, type: data.type };
    if (data.deleted === true) {
      trashMap[docSnap.id] = {
        timestamp: data.timestamp,
        type: data.type,
        deletedAt: data.deletedAt ?? data.timestamp,
      };
      return;
    }
    if (data.isPublic === true) info.isPublic = true;
    list.push(info);
    map[docSnap.id] = toProjectIndexEntry(info);
  });
  try {
    const userRef = doc(firestore, 'users', uid);
    await setDoc(userRef, { projectIndex: deleteField(), trashIndex: deleteField() }, { merge: true });
    await setDoc(userRef, { projectIndex: map, trashIndex: trashMap }, { merge: true });
  } catch (error) {
    console.error('Failed to write reconciled project index:', error);
  }
  return list;
};

// --- Trash (soft delete) index ----------------------------------------------
// Deleting a project no longer removes it: the project doc is flagged
// { deleted, deletedAt, purgeAt } and its index slot moves from `projectIndex`
// to a parallel `trashIndex` map on the user doc. The Trash view reads
// trashIndex; a native Firestore TTL policy on the `projects` collection group
// auto-deletes the doc once `purgeAt` passes (~30 days). This mirrors the
// projectIndex helpers above — same compact {timestamp,type} shape plus a
// deletedAt stamp for the purge countdown.
export interface TrashIndexEntry extends ProjectIndexEntry {
  deletedAt: number;
}

// NOTE: moving a project projectIndex → trashIndex (on delete) and back (on
// restore) is done inline via a writeBatch in useCloudProjects, so the
// project-doc flag and the index move commit atomically. There is intentionally
// no standalone helper for those two — a separate merge write could not share
// the batch.

// Drop a trash slot without restoring it (used by "Delete forever").
export const removeTrashIndexEntry = async (uid: string, title: string) => {
  try {
    await setDoc(doc(firestore, 'users', uid), { trashIndex: { [title]: deleteField() } }, { merge: true });
  } catch (error) {
    console.error('Failed to remove trash index entry:', error);
  }
};

// Read the Trash list off the user doc. Returns null when the field is absent
// (no trashed projects / pre-trash account), so callers can treat it as empty.
export const readTrashIndex = async (uid: string): Promise<ProjectInfo[] | null> => {
  const snap = await getDoc(doc(firestore, 'users', uid));
  const index = snap.data()?.trashIndex as Record<string, TrashIndexEntry> | undefined;
  if (!index) return null;
  const out: ProjectInfo[] = [];
  for (const [title, entry] of Object.entries(index)) {
    out.push({ title, timestamp: entry.timestamp, type: entry.type, deletedAt: entry.deletedAt });
  }
  return out;
};

// Rebuild a live ProjectState from a raw Firestore project document (or any
// object of the same shape — e.g. a classroom assignment / submission
// snapshot). Pure and synchronous: it walks PROJECT_DEFAULTS, overrides with
// values present in `data`, reconstructs Molecule class instances, applies the
// legacy adapters, and re-stamps runtime fields. `owner` and `title` are the
// per-document identity to apply (for a snapshot, pass the opening user's uid
// so a later Save lands in their own collection). It does NOT reconcile
// isPublic against the publicProjects index — callers that need that (see
// fetchProject) do it separately after this returns.
export const reconstructProjectState = (data: Record<string, any>, owner: string, title: string): ProjectState => {
  // Type-dependent default for the simulation container — drug-discovery
  // projects use a larger box than molecular-modeling projects. Polymer
  // chains can run long, so give Polymer Builder a roomier default.
  let containerSize: number;
  if (data.type === ProjectType.DRUG_DISCOVERY) {
    containerSize = 50;
  } else if (data.type === ProjectType.POLYMER_BUILDER) {
    containerSize = 30;
  } else {
    containerSize = 20;
  }
  const containerDefault: MolecularContainer = { lx: containerSize, ly: containerSize, lz: containerSize };

  // Legacy adapter: pre-numeric projects stored AI memory as the boolean
  // `independentPrompt`. Convert it before the merge so
  // `numberOfStepsToRemember` is populated from old documents.
  // independentPrompt: true → 0 (no memory); false → -1 (all).
  if (data.numberOfStepsToRemember === undefined && data.independentPrompt !== undefined) {
    data.numberOfStepsToRemember = data.independentPrompt ? 0 : -1;
  }
  // Legacy adapter: cloud docs that predate explicit per-project visibility
  // had either no `hiddenProperties` field, or a snapshot of the project-wide
  // default. In both cases the project owner didn't set a preference, so we
  // shouldn't retroactively hide bp/mp/density/etc. just because the default
  // has changed since. Treat both shapes as "show everything"; the
  // legacy-padding step below will still hide the four newer descriptors.
  const LEGACY_DEFAULT_HIDDEN = [
    'atomCount',
    'bondCount',
    'heavyAtomCount',
    'complexity',
    'density',
    'meltingPoint',
    'boilingPoint',
  ];
  const looksLikeDefault = (arr: unknown): boolean => {
    if (!Array.isArray(arr)) return false;
    const sorted = [...arr].sort();
    const currentDefaultSorted = [...PROJECT_DEFAULTS.hiddenProperties!].sort();
    const legacyDefaultSorted = [...LEGACY_DEFAULT_HIDDEN].sort();
    const eq = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
    return eq(sorted, currentDefaultSorted) || eq(sorted, legacyDefaultSorted);
  };
  if (
    data.hiddenProperties === undefined ||
    data.hiddenProperties === null ||
    looksLikeDefault(data.hiddenProperties)
  ) {
    data.hiddenProperties = [];
  }
  const merged = mergeWithDefaults(PROJECT_DEFAULTS, data);
  const ps: ProjectState = {
    ...merged,
    // runtime / per-document
    owner,
    title,
    type: data.type,
    timestamp: data.timestamp,
    key: data.key ?? String(data.timestamp ?? -1),
    time: data.time ?? dayjs(new Date(data.timestamp)).format('MM/DD/YYYY hh:mm A'),
    // type-dependent fallback only if molecularContainer is missing entirely
    molecularContainer: data.molecularContainer ?? containerDefault,
    // testMolecules need class reconstruction (Firestore stores plain objects)
    testMolecules: ModelUtil.reconstructMoleculesFromFirestore(data.testMolecules),
    // navigation camera falls back to the main camera if not present
    navPosition: data.navPosition ?? data.cameraPosition ?? PROJECT_DEFAULTS.navPosition,
    navRotation: data.navRotation ?? data.cameraRotation ?? PROJECT_DEFAULTS.navRotation,
    navUp: data.navUp ?? data.cameraUp ?? PROJECT_DEFAULTS.navUp,
    navTarget: data.navTarget ?? data.panCenter ?? PROJECT_DEFAULTS.navTarget,
  };
  for (const m of ps.testMolecules) {
    for (const n of ps.molecules) {
      if (n.name === m.name) {
        m.data = n.data;
        m.autoBond = n.autoBond;
        break;
      }
    }
  }
  // If a molecule's SDF carries a <PartialCharges> tag but the saved doc
  // didn't include the runtime atomCharges field, restore it from the SDF so
  // the popup and any charge-aware rendering work on load.
  rehydratePartialChargesFromSdf(ps.molecules);
  rehydratePartialChargesFromSdf(ps.testMolecules);
  // Push the molecule-level atomCharges into per-atom .charge so MD
  // electrostatics and the IR-spectrum dipole-current sampler (which both
  // read atom.charge) see partial charges on Examples-drawer loads. The
  // drag/place and edit-pipeline paths already do this; cloud load did not.
  applyAtomChargesToAtoms(ps.molecules);
  applyAtomChargesToAtoms(ps.testMolecules);
  // Refresh computed structural / descriptor / drug-likeness fields on
  // generated molecules. Old projects predate client-side computation and may
  // carry AI-fabricated values or missing pass flags.
  rehydrateComputedPropertiesFromSdf(ps.molecules, ps.generatedMolecularProperties);
  rehydrateComputedPropertiesFromSdf(ps.testMolecules, ps.generatedMolecularProperties);
  // One-shot migration: hide the four newer structurally-derived descriptors
  // on legacy projects that predate the feature.
  padHiddenPropertiesForLegacyProjects(ps);
  // One-shot migration: fold a legacy single `biomolecule` into `biomolecules`.
  migrateBiomoleculesField(ps);
  // One-shot migration: retired AI tiers (notably DeepSeek's v4-pro) become the
  // tier that actually runs.
  migrateRetiredAiProvider(ps);
  // Migration: older projects may hold unnamed crystals, which nothing could
  // delete or rename (everything addresses gallery crystals by name).
  migrateUnnamedCrystals(ps);
  // A crystal-core project with structures in its gallery never opens with an
  // empty lab (examples open chat-first, with nothing selected).
  fillEmptyLabFromGallery(ps, ps.type);
  // Migration: legacy per-panel visibility flags → unified analysis panel.
  // Projects saved before the tabbed analysis panel used three separate
  // booleans (energyGraphVisible / speedGraphVisible / irSpectrumPanelVisible)
  // and three sim-bar buttons. The new model is one panel with tabs. If
  // the doc has no analysisPanelVisible set yet, decide what to show
  // based on which legacy flag(s) were on. Priority for the initial tab:
  // IR > Energy > Speed (the newer feature wins on the assumption it's
  // what the user was most recently looking at). Gated on the raw `data`
  // (not `ps`) because mergeWithDefaults always populates
  // analysisPanelVisible from PROJECT_DEFAULTS, so `ps.analysisPanelVisible`
  // is never undefined here. Re-saves of new docs roundtrip unchanged.
  if (data.analysisPanelVisible === undefined) {
    const anyLegacyOn = ps.energyGraphVisible || ps.speedGraphVisible || ps.irSpectrumPanelVisible;
    if (anyLegacyOn) {
      ps.analysisPanelVisible = true;
      ps.activeAnalysisTab = ps.irSpectrumPanelVisible ? 'ir' : ps.energyGraphVisible ? 'energy' : 'speed';
    }
  }
  // Polymer Builder default: a fresh polymer project (no `activeAnalysisTab`
  // in the saved doc) lands on the polymer tab. If the user previously
  // navigated to a different tab and saved, the saved value wins — only
  // unset/missing falls back to 'polymer'.
  if (data.activeAnalysisTab === undefined && data.type === ProjectType.POLYMER_BUILDER) {
    ps.activeAnalysisTab = 'polymer';
  }
  // Polymer Builder: ensure drug-likeness + complexity descriptors are in
  // hiddenProperties on every load (idempotent). New projects already get
  // this from createDefaultProjectState; older saved projects predate the
  // polymer-specific defaults and would otherwise show empty / broken
  // axes for these monomer-irrelevant fields.
  if (data.type === ProjectType.POLYMER_BUILDER) {
    const required = [
      'logP',
      'hydrogenBondDonorCount',
      'hydrogenBondAcceptorCount',
      'polarSurfaceArea',
      'fsp3',
      'qed',
      'complexity',
    ];
    const hidden = new Set<string>(ps.hiddenProperties ?? []);
    let changed = false;
    for (const k of required) {
      if (!hidden.has(k)) {
        hidden.add(k);
        changed = true;
      }
    }
    if (changed) ps.hiddenProperties = Array.from(hidden);
  }
  // Chat log: prefer the gzip'd Bytes field; fall back to the legacy inline
  // `chatMessages` array (mergeWithDefaults already copied it into ps) for docs
  // saved before compression. Either way the store ends up with plain objects;
  // strip the compressed field so a Bytes value never leaks into projectState.
  const gz = toUint8((data as Record<string, any>)[CHAT_GZ_FIELD]);
  if (gz) {
    try {
      ps.chatMessages = decodeChatMessages(gz);
    } catch (e) {
      console.warn('chat decompress failed; falling back to inline/none', e);
    }
  }
  delete (ps as unknown as Record<string, unknown>)[CHAT_GZ_FIELD];

  // Biomolecular-Lab structures: re-attach the no-url structures' PDB text from
  // the gzip'd sidecar, matched by name. Legacy docs have inline `data` and no
  // sidecar — those entries keep whatever data mergeWithDefaults copied in.
  const bioGz = toUint8((data as Record<string, any>)[BIO_GZ_FIELD]);
  if (bioGz) {
    try {
      const map = decodeBiomoleculeData(bioGz);
      for (const b of ps.biomolecules ?? []) {
        if (!b.data && b.name && map[b.name] !== undefined) b.data = map[b.name];
      }
    } catch (e) {
      console.warn('biomolecule decompress failed; falling back to inline/none', e);
    }
  }
  delete (ps as unknown as Record<string, unknown>)[BIO_GZ_FIELD];
  return ps;
};

export const fetchProject = async (userid: string, project: string, setProjectState: (ps: ProjectState) => void) => {
  const lang = { lng: useStore.getState().language };
  // An AI turn still running belongs to the project being left. Its tools poll
  // the lab (a melting sweep runs for minutes) and its chat hook appends the
  // final message to whatever projectState is current when it ends — which,
  // without this, was the NEXT project: the salt sweep's report turned up in
  // the copper example, and copper's in the ice example. Abort it here, and
  // drop any chart it had pushed so far, before the new project is installed.
  cancelCurrentGeneration();
  clearAgentChatAttachments();
  try {
    const docRef = doc(firestore, 'users', userid, 'projects', project);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // A soft-deleted project still has a doc (until TTL purges it), but it
      // lives in Trash — don't open it from a stale Recent entry or deep link,
      // or the user could edit & re-save it, resurrecting it out of band.
      if (data?.deleted === true) {
        setMessage('info', i18n.t('message.ProjectIsInTrash', lang) + ': ' + project);
        return;
      }
      if (data) {
        const ps = reconstructProjectState(data, userid, docSnap.id);
        // Reconcile the project's isPublic flag against the publicProjects
        // index. The index is the source of truth — a `true` flag on the
        // project doc with no matching index entry is stale (e.g. an earlier
        // publish was rolled back, or the Commons entry was removed by a
        // separate client while a stale projectState was in memory). Skipping
        // when the flag is already false saves a read for the common case.
        if (ps.isPublic === true) {
          try {
            const publicSnap = await getDoc(doc(firestore, 'publicProjects', `${userid}_${project}`));
            if (!publicSnap.exists()) ps.isPublic = false;
          } catch (e) {
            console.warn('publicProjects existence check failed; trusting project doc', e);
          }
        }
        setProjectState(ps);
      } else {
        setMessage('error', i18n.t('message.CannotOpenProject', lang) + ': ' + project);
      }
    } else {
      // The doc is gone (e.g. a class submission points at a project the
      // student has since renamed or deleted, or one that was never saved to
      // the cloud). Without this branch fetchProject silently no-ops and the
      // caller is left staring at a blank app — surface it instead.
      setMessage('error', i18n.t('message.ProjectNoLongerExists', lang) + ': ' + project);
    }
  } catch (e) {
    setMessage('error', i18n.t('message.CannotOpenProject', lang) + ': ' + e);
  }
};

export const postFetch = () => {
  useDataStore.getState().speedArrayMap.clear();
};

// Doc id for the publicProjects index. Combines owner uid and title so an
// entry is uniquely keyed by the project's home (matches the read URL).
// Titles only contain REGEX_ALLOWABLE_IN_NAME characters, so the underscore
// separator stays parseable if we ever need to round-trip.
export const publicProjectDocId = (ownerUid: string, title: string) => `${ownerUid}_${title}`;

// Flip the project's isPublic flag and write a discoverability entry to the
// top-level publicProjects collection. The actual project doc stays at
// users/{uid}/projects/{title} — public viewers read it directly with the
// existing ?userid=&project= URL flow.
// Thumbnails live in Firebase Storage at this path. The owner uid is its
// own path segment so Storage rules can check write authorization with a
// single `request.auth.uid == uid` predicate — no parsing required.
const thumbnailStoragePath = (uid: string, title: string) => `commonThumbnails/${uid}/${title}.jpg`;

// Upload a data-URL thumbnail to Storage and return the public download
// URL. Returns null on failure so the caller can publish without a
// thumbnail rather than aborting the whole flow.
export const uploadThumbnail = async (uid: string, title: string, dataUrl: string): Promise<string | null> => {
  try {
    const r = storageRef(storage, thumbnailStoragePath(uid, title));
    await uploadString(r, dataUrl, 'data_url');
    return await getDownloadURL(r);
  } catch (error) {
    console.error('uploadThumbnail failed', error);
    return null;
  }
};

// Best-effort delete on unpublish. Missing object isn't an error worth
// surfacing — most often it just means the entry predates this migration.
export const deleteThumbnail = async (uid: string, title: string): Promise<void> => {
  try {
    const r = storageRef(storage, thumbnailStoragePath(uid, title));
    await deleteObject(r);
  } catch (error) {
    const code = (error as { code?: string })?.code;
    if (code !== 'storage/object-not-found') {
      console.error('deleteThumbnail failed', error);
    }
  }
};

// Per-structure gallery-card snapshots for the Protein / DNA-RNA labs. Each
// owner uid is its own path segment so a Storage rule can authorize writes with
// a single `request.auth.uid == uid` predicate; the structure name is
// URL-encoded because (unlike the constrained project title) it can hold spaces
// and other characters illegal in a Storage path.
const bioThumbnailStoragePath = (uid: string, title: string, name: string) =>
  `bioThumbnails/${uid}/${title}/${encodeURIComponent(name)}.jpg`;

// Persist freshly-captured biomolecule card thumbnails to Storage and stamp the
// resulting download URL onto each structure, so the gallery cards survive a
// reload. The JPEGs are far too large to inline in the 1 MiB Firestore doc, so
// only the URL rides on the project. Mutates `biomolecules` in place. Best-
// effort: only structures whose live cache holds a NEW data-URL snapshot are
// uploaded (a structure not viewed this session keeps whatever `thumbnailUrl`
// it loaded with); a failed upload just leaves that one card unpersisted.
// What each Storage path last received this session (the snapshot data-URL) and
// the URL it got: a save uploads a picture only when it is new or changed since,
// instead of re-sending every viewed structure's JPEG on every save (school
// bandwidth). Keyed by the full Storage path, so another project or a renamed
// structure uploads afresh.
const uploadedBioThumbnails = new Map<string, { snapshot: string; url: string }>();

export const uploadBioThumbnails = async (
  uid: string,
  title: string,
  biomolecules: MoleculeInterface[],
  cache: Record<string, string>,
): Promise<void> => {
  await Promise.all(
    biomolecules.map(async (b) => {
      const snapshot = cache[b.name];
      if (!snapshot || !snapshot.startsWith('data:')) return;
      const path = bioThumbnailStoragePath(uid, title, b.name);
      const done = uploadedBioThumbnails.get(path);
      if (done && done.snapshot === snapshot) {
        b.thumbnailUrl = done.url; // already in Storage — just stamp its URL
        return;
      }
      try {
        const r = storageRef(storage, path);
        await uploadString(r, snapshot, 'data_url');
        b.thumbnailUrl = await getDownloadURL(r);
        uploadedBioThumbnails.set(path, { snapshot, url: b.thumbnailUrl });
      } catch (error) {
        console.error('uploadBioThumbnails failed for', b.name, error);
      }
    }),
  );
};

// Delete the Storage thumbnails for structures that are no longer in the saved
// project (deleted from the gallery, or renamed). Run AFTER a successful save so
// cleanup tracks the durable state — deleting on the gallery click instead would
// strand the still-saved doc's URL on a deleted blob (broken image on reopen)
// and break undo. Lists the project's thumbnail folder and removes any object
// whose decoded name isn't in `keepNames`. Best-effort: a missing folder or a
// failed delete just leaves a harmless orphan (storage rules grant list via
// `allow read`). The `.jpg` object names are encodeURIComponent(name) — see
// bioThumbnailStoragePath — so decode before comparing.
export const reconcileBioThumbnails = async (uid: string, title: string, keepNames: string[]): Promise<void> => {
  try {
    const keep = new Set(keepNames);
    const folder = storageRef(storage, `bioThumbnails/${uid}/${title}`);
    const listing = await listAll(folder);
    await Promise.all(
      listing.items.map(async (item) => {
        const encoded = item.name.replace(/\.jpg$/i, '');
        let name = encoded;
        try {
          name = decodeURIComponent(encoded);
        } catch {
          // Non-decodable object name — fall back to the raw form for the compare.
        }
        if (!keep.has(name)) await deleteObject(item);
      }),
    );
  } catch (error) {
    console.error('reconcileBioThumbnails failed', error);
  }
};

export const publishProject = async (
  uid: string,
  title: string,
  meta: {
    type: ProjectType;
    timestamp: number;
    description?: string | null;
    ownerName?: string | null;
    thumbnail?: string | null;
  },
) => {
  try {
    const projectRef = doc(firestore, 'users', uid, 'projects', title);
    await updateDoc(projectRef, { isPublic: true });

    const publicRef = doc(firestore, 'publicProjects', publicProjectDocId(uid, title));
    const entry: PublicProjectInfo = {
      ownerUid: uid,
      ownerName: meta.ownerName ?? null,
      title,
      type: meta.type,
      timestamp: meta.timestamp,
      publishedAt: Date.now(),
      description: meta.description ?? null,
    };
    // Publish the entry with the inline thumbnail first so viewers get
    // an instant preview, then try to upgrade to a Storage URL in the
    // background. Awaiting the upload would stall the share UX on slow
    // / blocked Storage (missing bucket, undeployed rules). When the
    // upgrade lands we drop the inline base64 to keep the doc small;
    // if Storage stays broken, the inline preview just persists.
    if (meta.thumbnail) entry.thumbnail = meta.thumbnail;
    await setDoc(publicRef, entry);
    if (meta.thumbnail) {
      uploadThumbnail(uid, title, meta.thumbnail).then((url) => {
        if (!url) return;
        updateDoc(publicRef, { thumbnailUrl: url, thumbnail: deleteField() }).catch((e) => {
          console.error('failed to patch thumbnailUrl after upload', e);
        });
      });
    }
    return true;
  } catch (error) {
    console.error('publishProject failed', error);
    return false;
  }
};

// Keep a published project's Commons entry in sync with the project on each
// save, so the public card reflects real edits — not just the original publish.
// Refreshes `timestamp` (drives the "most recent" sort) plus the description /
// owner name / thumbnail that show on the card. Like/view counts and the likes
// subcollection are left untouched. Best-effort: a missing entry (never
// published) or a transient failure is swallowed, since the save itself has
// already committed by the time this runs. Callers should fire-and-forget.
//
// Thumbnail handling mirrors publishProject: write the inline base64 for an
// instant refresh, clear any stale Storage URL so the card shows the new
// preview right away, then upgrade to a Storage URL in the background and drop
// the inline copy to keep the doc small. Only fields explicitly provided are
// written (description/ownerName are skipped when `undefined`, so a partial
// caller can't blank them); a null is written through as a deliberate clear.
export const syncPublicProjectMeta = async (
  uid: string,
  title: string,
  meta: {
    timestamp: number;
    description?: string | null;
    ownerName?: string | null;
    thumbnail?: string | null;
  },
): Promise<void> => {
  try {
    const publicRef = doc(firestore, 'publicProjects', publicProjectDocId(uid, title));
    // `any` so deleteField() (a FieldValue) sits alongside scalars without
    // tripping updateDoc's union-typed value parameter.
    const update: Record<string, any> = { timestamp: meta.timestamp };
    if (meta.description !== undefined) update.description = meta.description ?? null;
    if (meta.ownerName !== undefined) update.ownerName = meta.ownerName ?? null;
    if (meta.thumbnail) {
      update.thumbnail = meta.thumbnail;
      // Drop the previous Storage URL so the card (which prefers thumbnailUrl)
      // falls back to the fresh inline preview until the upload below lands.
      update.thumbnailUrl = deleteField();
    }
    await updateDoc(publicRef, update);
    if (meta.thumbnail) {
      uploadThumbnail(uid, title, meta.thumbnail).then((url) => {
        if (!url) return;
        updateDoc(publicRef, { thumbnailUrl: url, thumbnail: deleteField() }).catch((e) => {
          console.error('failed to patch thumbnailUrl after save-sync', e);
        });
      });
    }
  } catch (error) {
    // not-found just means the project isn't published; nothing to sync.
    const code = (error as { code?: string })?.code;
    if (code !== 'not-found') console.warn('syncPublicProjectMeta failed', error);
  }
};

// Inverse of publishProject. Clears the flag on the source doc and removes
// the index entry. Safe to call when not published (writes are idempotent).
export const unpublishProject = async (uid: string, title: string) => {
  try {
    const projectRef = doc(firestore, 'users', uid, 'projects', title);
    await updateDoc(projectRef, { isPublic: false });

    const publicRef = doc(firestore, 'publicProjects', publicProjectDocId(uid, title));
    await deleteDoc(publicRef);

    // Storage cleanup runs as fire-and-forget. Awaiting it would let a
    // slow / blocked Storage call (e.g. unreachable bucket, missing
    // rules) stall the whole unpublish UX even though the Firestore
    // writes already succeeded. Orphaning a thumbnail is preferable.
    void deleteThumbnail(uid, title);
    return true;
  } catch (error) {
    console.error('unpublishProject failed', error);
    return false;
  }
};

// Copy a published project into the calling user's own subcollection so
// they can experiment with it independently. The source is read under the
// existing isPublic == true rule; the destination write is owner-only.
// Title collision is handled by appending "(forked)" and incrementing a
// numeric suffix until a free slot is found. Returns the new title on
// success, null on failure.
export const forkProject = async (
  forkerUid: string,
  source: { ownerUid: string; title: string },
): Promise<string | null> => {
  try {
    const sourceRef = doc(firestore, 'users', source.ownerUid, 'projects', source.title);
    const snap = await getDoc(sourceRef);
    if (!snap.exists()) return null;
    const data = snap.data() as Partial<ProjectState>;

    // Find a non-colliding title in the forker's own collection. Sequential
    // existence checks — typical case is one read.
    const base = `${source.title} (forked)`;
    let candidate = base;
    let n = 1;
    while (true) {
      const candidateRef = doc(firestore, 'users', forkerUid, 'projects', candidate);
      const exists = (await getDoc(candidateRef)).exists();
      if (!exists) break;
      n++;
      candidate = `${source.title} (forked ${n})`;
    }

    // Deep clone via JSON round-trip — strips any non-serializable cruft
    // the SDK may have attached. Then re-stamp owner/title/timestamps so
    // the new doc is unambiguously the forker's.
    const cloned = JSON.parse(JSON.stringify(data)) as Partial<ProjectState> & Record<string, unknown>;
    // The compressed chat + biomolecule sidecars are Firestore Bytes values;
    // JSON.stringify mangles them, so copy the original (immutable) references
    // back onto the clone so the fork keeps its chat log and structure data.
    if ((data as Record<string, unknown>)[CHAT_GZ_FIELD]) {
      cloned[CHAT_GZ_FIELD] = (data as Record<string, unknown>)[CHAT_GZ_FIELD];
    }
    if ((data as Record<string, unknown>)[BIO_GZ_FIELD]) {
      cloned[BIO_GZ_FIELD] = (data as Record<string, unknown>)[BIO_GZ_FIELD];
    }
    const now = Date.now();
    cloned.owner = forkerUid;
    cloned.title = candidate;
    cloned.timestamp = now;
    cloned.key = String(now);
    cloned.time = dayjs(now).format('MM/DD/YYYY hh:mm A');
    delete cloned.isPublic;

    const destRef = doc(firestore, 'users', forkerUid, 'projects', candidate);
    await setDoc(destRef, cloned);

    // Keep the forker's project index current so the new fork shows up in their
    // My Projects list without a reconcile scan.
    try {
      await setProjectIndexEntry(forkerUid, {
        timestamp: now,
        title: candidate,
        type: (cloned as { type?: ProjectType }).type as ProjectType,
      });
    } catch (e) {
      console.error('forkProject: failed to update project index', e);
    }

    return candidate;
  } catch (error) {
    console.error('forkProject failed', error);
    return null;
  }
};

// One-shot fetch of the latest published projects. The gallery handles its
// own client-side search + pagination over this list. Capped to keep reads
// bounded; bump or paginate if the public corpus grows.
//
// Ordered by `timestamp` (last-saved) to match the Commons' default sort, not
// `publishedAt` (first-published). With a cap, the order key decides which
// entries make the window: ordering by publishedAt would drop a project that
// was published long ago but re-saved recently, so it could never surface at
// the top of "most recent" even though its timestamp is fresh. Single-field
// index on `timestamp` is auto-created by Firestore — no console change.
export const fetchPublicProjects = async (max = 500): Promise<PublicProjectInfo[]> => {
  try {
    const q = query(collection(firestore, 'publicProjects'), orderBy('timestamp', 'desc'), limit(max));
    const snap = await getDocs(q);
    const out: PublicProjectInfo[] = [];
    snap.forEach((d) => out.push(d.data() as PublicProjectInfo));
    return out;
  } catch (error) {
    console.error('fetchPublicProjects failed', error);
    return [];
  }
};

// Bump the Commons entry's view counter by 1. Atomic — multiple viewers
// clicking at once each get their own +1 without read-modify-write races.
// Returns the underlying promise so the caller can await it (same-tab
// navigation would otherwise cancel the in-flight write and the bump
// would never reach the server). Allowed for any caller by a Firestore
// rule that lets `viewCount` increment in isolation.
export const incrementViewCount = async (ownerUid: string, title: string): Promise<void> => {
  const ref = doc(firestore, 'publicProjects', publicProjectDocId(ownerUid, title));
  try {
    await updateDoc(ref, { viewCount: increment(1) });
  } catch (e) {
    console.warn('incrementViewCount failed', e);
  }
};

// Likes are stored as a subcollection of each publicProjects entry:
//   publicProjects/{docId}/likes/{userUid}
// Doc presence = the user has liked it. Subcollection rather than an array
// on the parent doc so rules can grant write to non-owner users without
// also letting them touch the parent's metadata. Counts are read on demand
// via a server-side aggregation query (one round-trip per project).
export const likeProject = async (forkerUid: string, ownerUid: string, title: string) => {
  try {
    const likeRef = doc(firestore, 'publicProjects', publicProjectDocId(ownerUid, title), 'likes', forkerUid);
    await setDoc(likeRef, { at: Date.now() });
    return true;
  } catch (error) {
    console.error('likeProject failed', error);
    return false;
  }
};

export const unlikeProject = async (forkerUid: string, ownerUid: string, title: string) => {
  try {
    const likeRef = doc(firestore, 'publicProjects', publicProjectDocId(ownerUid, title), 'likes', forkerUid);
    await deleteDoc(likeRef);
    return true;
  } catch (error) {
    console.error('unlikeProject failed', error);
    return false;
  }
};

// Batched lookup for a page of cards: returns { count, likedByMe } for each
// project, keyed by `${ownerUid}_${title}`. Issues two reads per project
// (count + me-liked) in parallel. `me` may be null for anonymous viewers.
export const fetchLikesForList = async (
  me: string | null,
  rows: { ownerUid: string; title: string }[],
): Promise<Record<string, { count: number; likedByMe: boolean }>> => {
  const out: Record<string, { count: number; likedByMe: boolean }> = {};
  await Promise.all(
    rows.map(async (r) => {
      const id = publicProjectDocId(r.ownerUid, r.title);
      const likesCol = collection(firestore, 'publicProjects', id, 'likes');
      const countPromise = getCountFromServer(likesCol)
        .then((s) => s.data().count)
        .catch(() => 0);
      const likedPromise = me
        ? getDoc(doc(firestore, 'publicProjects', id, 'likes', me))
            .then((s) => s.exists())
            .catch(() => false)
        : Promise.resolve(false);
      const [count, likedByMe] = await Promise.all([countPromise, likedPromise]);
      out[id] = { count, likedByMe };
    }),
  );
  return out;
};

export const getImageData = (image: HTMLImageElement) => {
  const c = document.createElement('canvas');
  c.width = image.width;
  c.height = image.height;
  const ctx = c.getContext('2d');
  if (ctx) {
    ctx.drawImage(image, 1, 1); // 1 is for padding
  }
  return c.toDataURL();
};
